import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";

const STATUSES = ["new", "contacted", "approved", "rejected", "archived"];
const STATUS_STYLES = {
  new: "bg-[var(--cl-primary)]/20 text-[var(--cl-primary)]",
  contacted: "bg-[var(--cl-gold)]/20 text-[var(--cl-gold)]",
  approved: "bg-emerald-500/20 text-emerald-400",
  rejected: "bg-zinc-500/20 text-zinc-400",
  archived: "bg-zinc-800 text-zinc-500",
};

export default function AdminAffiliates() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(async () => {
    try { setRows((await api.get("/admin/affiliate-applications")).data); }
    catch { toast.error("Failed to load applications"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status, admin_notes) => {
    try {
      await api.put(`/admin/affiliate-applications/${id}`, { status, admin_notes });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await api.delete(`/admin/affiliate-applications/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div>
      <h1 className="font-display text-4xl mb-2">Affiliate Applications</h1>
      <p className="text-zinc-400 text-sm mb-6">Partnership inquiries from the public affiliates page.</p>

      {rows.length === 0 && <div className="text-zinc-500 text-sm">No applications yet.</div>}

      <div className="space-y-3" data-testid="affiliate-list">
        {rows.map((a) => {
          const isOpen = openId === a.id;
          return (
            <div key={a.id} className="border border-white/10 rounded bg-[var(--cl-surface)]"
                 data-testid={`aff-row-${a.id}`}>
              <div className="p-4 flex flex-wrap items-start gap-4 justify-between cursor-pointer"
                   onClick={() => setOpenId(isOpen ? null : a.id)}>
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">{a.email} · {a.created_at?.slice(0, 10)}</div>
                  {a.platform_links && <div className="text-xs text-[var(--cl-orange)] mt-1">{a.platform_links}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] px-2 py-1 rounded uppercase tracking-widest ${STATUS_STYLES[a.status] || ""}`}>
                    {a.status}
                  </span>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-white/5 p-4 space-y-4">
                  {a.message && (
                    <div>
                      <div className="text-label text-zinc-500 mb-1">Message</div>
                      <p className="text-sm text-zinc-300 whitespace-pre-wrap">{a.message}</p>
                    </div>
                  )}
                  <div>
                    <div className="text-label text-zinc-500 mb-2">Status</div>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button key={s}
                                onClick={() => setStatus(a.id, s, a.admin_notes)}
                                data-testid={`aff-status-${a.id}-${s}`}
                                className={`text-xs px-3 py-1 rounded border transition-colors ${a.status === s ? "bg-[var(--cl-primary)] border-[var(--cl-primary)] text-white" : "border-white/15 text-zinc-300 hover:border-[var(--cl-orange)]"}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-label text-zinc-500 mb-1">Admin notes</div>
                    <textarea rows={3}
                              value={notesDraft[a.id] ?? a.admin_notes ?? ""}
                              onChange={(e) => setNotesDraft({ ...notesDraft, [a.id]: e.target.value })}
                              data-testid={`aff-notes-${a.id}`}
                              className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm" />
                    <button onClick={() => setStatus(a.id, a.status, notesDraft[a.id] ?? a.admin_notes)}
                            className="btn-outline text-xs mt-2">Save notes</button>
                  </div>
                  <div className="pt-4 border-t border-white/5 flex justify-end">
                    <button onClick={() => del(a.id)}
                            data-testid={`aff-delete-${a.id}`}
                            className="text-xs text-zinc-500 hover:text-[var(--cl-primary)] flex items-center gap-1">
                      <Trash2 size={12}/> Delete permanently
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
